/**
 * UnifiedActorEditor — Wave E + F + G (AC-EDIT-1/3/4/5, MODEL-3, IMG-1/2/3/5/6)
 *
 * ONE slide-in editor sheet that creates/edits all three actor types:
 *   - human           → identity (read-only user link + account avatar), domains, visibility, deactivate
 *   - ai_operator     → full editor: identity (direct image upload), model/instructions,
 *                       capabilities, delegation, budget, role contract, persona (incl. domain_stances),
 *                       governance, management (visibility, readiness/promote, deactivate)
 *   - knowledge_persona → identity (direct image upload + expertise), domains, persona (incl. domain_stances),
 *                       management (visibility, deactivate)
 *
 * Wave F additions (AC-IMG-2/3/6):
 *   - Identity tab: ActorDirectImageUpload replaces ImageAttachmentField for ai_operator + knowledge_persona
 *   - Human identity shows account avatar read-only via Avatar component (AC-IMG-5 — no upload control)
 *
 * Wave G additions (AC-IMG-4/5 confirmed):
 *   - AC-IMG-4: no emoji icon field anywhere in the editor (also cleaned from AiOperatorEditor)
 *   - AC-IMG-5: human account avatar shown read-only in humanSection; no upload affordance
 *
 * Wave E additions:
 *   - AC-EDIT-3: Management tab — visibility, readiness history, Promote, deactivate
 *   - AC-EDIT-4: View button + /roros/actors/:id removed — editor IS the view
 *   - AC-EDIT-5: Promote (ReadinessAssessmentDialog) accessible from the editor
 *   - AC-EDIT-1 finalize: knowledge_persona + human now open this editor (not ActorFormDialog)
 *   - AC-MODEL-3 finalize: domain_stances added to Persona tab
 *
 * AC-MOD-6: moderator toggles removed. Replaced by:
 *   - Capability display (can_moderate_meetings / can_facilitate_decisions)
 *   - "Set as org default meeting moderator" / "Set as org default decision moderator"
 *
 * AC-MODEL-5: Protected (system_role ≠ null) actors:
 *   - Show a "System" badge in the editor header
 *   - Name field is read-only/disabled
 *   - Deactivate button hidden
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { FormSheet } from '@/components/ui/FormSheet';
import type { TabDef } from '@/components/ui/FormSheet';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Switch } from '@/components/ui/Switch';
import { CardSelector } from '@/components/ui/CardSelector';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar/Avatar';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { LoadingState } from '@/components/ui/LoadingState';
import { showToast } from '@/components/ui/Toast';
import { Alert } from '@/components/ui/Alert';
import {
  Bot, Cpu, Zap, X, BookOpen, User, Lock, ShieldCheck, PowerOff, Plus, History,
} from 'lucide-react';
import { t } from '@/lib/i18n';
import { formatDate } from '@/lib/localeFormatters';
import {
  useRorosActorCreate,
  useRorosActorUpdate,
  useRorosActorAssignDomain,
  useRorosActorUnassignDomain,
  useRorosActorChangeAdoptionLevel,
} from '@/hooks/roros/actors';
import {
  useRorosAgentCreate,
  useRorosAgentUpdate,
} from '@/hooks/roros/agents';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { useOrgMembers, useActiveOrgId } from '@/hooks/queries';
import { RorosActorType, RorosAdoptionLevel } from '@/types/roros/enums';
import type { RorosActorDTO } from '@/types/roros/actors';
import type {
  RorosAgentDefinitionDTO,
  RorosDelegationConfig,
} from '@/services/roros/ai-operators.service';
import type { RorosDomainDTO } from '@/types/roros/domains';
import { UserPicker } from './UserPicker';
import { ActorDirectImageUpload } from '@/components/roros/ActorDirectImageUpload';
import { VisibilityPanel } from '@/components/roros/visibility/VisibilityPanel';
import { ReadinessAssessmentDialog } from './ReadinessAssessmentDialog';
import { KnowledgeUploadSheet } from '@/components/roros/KnowledgeUploadSheet';
import {
  useRorosPersonaPinDenyList,
  useRorosPersonaReachableCount,
  useRorosPersonaPinDenyCreate,
  useRorosPersonaPinDenyDelete,
  useRorosKnowledgeCreate,
} from '@/hooks/roros/artifacts';
import { DOMAIN_LIST } from '@/routes/roros/launchpad/types';
import {
  adoptionLevelLabel,
  arrayUnordered,
  buildDelegationConfig,
  buildProviderOptions,
  diffSnapshots,
  IDENTITY_FIELDS,
  KP_OPERATOR_FIELDS,
  OPERATOR_FIELDS,
  readinessResultLabel,
  TRIGGER_IMPACT_OPTIONS,
  type FormSnapshot,
  type ActorEditorAiConfig,
  type TriggerImpact,
} from './UnifiedActorEditor.model';
import { useActorToolGrants, DOMAIN_TOOLS } from './hooks/useActorToolGrants';
import { useActorSkills } from './hooks/useActorSkills';
import { useActorRoleContract } from './hooks/useActorRoleContract';
import { useActorLifecycle } from './hooks/useActorLifecycle';
import { useActorPersona } from './hooks/useActorPersona';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UnifiedActorEditorProps {
  isOpen: boolean;
  onClose: () => void;
  /** Existing actor being edited. Null = create mode. */
  actor?: RorosActorDTO | null;
  /** For ai_operators in edit mode: the existing agent definition. */
  agentDefinition?: RorosAgentDefinitionDTO | null;
  /** Org-wide domains list. */
  domains: RorosDomainDTO[];
  /** When set in create mode, pre-selects the actor type. */
  initialActorType?: string;
  /** URL routing support — optional deep-link key/id. */
  urlKey?: string;
  urlId?: string;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function UnifiedActorEditor({
  isOpen,
  onClose,
  actor,
  agentDefinition,
  domains,
  initialActorType,
  urlKey,
  urlId,
}: UnifiedActorEditorProps) {
  const isEdit = !!actor;
  const isAiOp = actor?.actorType === RorosActorType.AI_OPERATOR;
  const isKnowledgePersona = actor?.actorType === RorosActorType.KNOWLEDGE_PERSONA;
  const showPersona = isEdit && (isAiOp || isKnowledgePersona);
  const isProtected = !!(actor?.systemRole);

  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.ROROS_ACTORS_MANAGE);
  const canManageSkills = hasPermission(PERMISSIONS.ROROS_AGENT_SKILLS_MANAGE);
  const canManageKnowledge = hasPermission(PERMISSIONS.MANAGE_KNOWLEDGE);

  // ─── Mutations (shared) ───────────────────────────────────────────────────
  const createActorMutation = useRorosActorCreate();
  const updateActorMutation = useRorosActorUpdate();
  const assignDomainMutation = useRorosActorAssignDomain();
  const unassignDomainMutation = useRorosActorUnassignDomain();
  const changeAdoptionMutation = useRorosActorChangeAdoptionLevel();

  // ─── Mutations (AI operator) ─────────────────────────────────────────────
  const createAgentMutation = useRorosAgentCreate();
  const updateAgentMutation = useRorosAgentUpdate();

  // ─── Skills (AI operator) ────────────────────────────────────────────────
  const {
    matrixQuery,
    assignSkillMutation,
    unassignSkillMutation,
    pickerSkillId,
    setPickerSkillId,
    assignedSkills,
    availableSkillOptions,
    handleSkillAssign,
    handleSkillUnassign,
  } = useActorSkills({ agentDefinition });

  // ─── Tool grants (AGS-M2) ────────────────────────────────────────────────
  const { allowedTools, toolGrantsSaving, handleToolToggle, handleToolGrantsSave } =
    useActorToolGrants({ isOpen, agentDefinition, updateAgentMutation });

  // ─── Delegation trigger impacts (decision-moderator rules; synced from agentDefinition) ──
  const [decisionTriggers, setDecisionTriggers] = useState<TriggerImpact[]>(['root', 'trunk']);

  // ─── Role contract ───────────────────────────────────────────────────────
  const {
    linkedRoleContract,
    isContractActive,
    isLinkedContractEditable,
    assignableContracts,
    pickerContractId,
    contractAssigning,
    handleContractSelect,
    handleContractUnlink,
  } = useActorRoleContract({ actor, isOpen });

  // ─── Persona (AC-EDIT-2 / AC-MODEL-3 finalized) ──────────────────────────
  // Shown for ai_operator AND knowledge_persona (Wave E: EDIT-1 finalize).
  // Backend _assertPersonaEligible only blocks humans; all other types allowed.
  const {
    personaQuery,
    upsertPersonaMutation,
    personaState,
    setPersonaState,
    personaInitialized,
    personaDirty,
    setPersonaDirty,
    stanceMap,
    handleDomainStanceChange,
    handlePersonaSave,
  } = useActorPersona({ actor, isOpen, showPersona, isKnowledgePersona });

  // ─── Lifecycle: readiness assessments + deactivate ───────────────────────
  const {
    deactivateMutation,
    assessmentsQuery,
    showPromoteDialog,
    setShowPromoteDialog,
    showDeactivateConfirm,
    setShowDeactivateConfirm,
    handleDeactivate,
  } = useActorLifecycle({ actor, isEdit, isAiOp, onClose });

  // ─── Linked-user (for human actors in edit mode) ─────────────────────────
  const orgId = useActiveOrgId() ?? undefined;

  // ─── AI configs (for provider selector) ──────────────────────────────────
  const { data: aiConfigs = [] } = useQuery<ActorEditorAiConfig[]>({
    queryKey: ['ai-configs', orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const res = await apiFetch(`/api/orgs/${orgId}/ai-config`);
      const data = await res.json();
      return data.success ? (data.data ?? []) : [];
    },
    enabled: !!orgId && isOpen,
  });

  const membersQuery = useOrgMembers(
    isEdit && actor?.actorType === RorosActorType.HUMAN ? orgId : undefined,
  );
  const linkedMember = useMemo(() => {
    if (!isEdit || actor?.actorType !== RorosActorType.HUMAN || !actor?.userId) return null;
    return (membersQuery.data ?? []).find((m: any) => m.user_id === actor.userId) ?? null;
  }, [isEdit, actor?.actorType, actor?.userId, membersQuery.data]);

  // ─── Capability draft ─────────────────────────────────────────────────────
  const [capabilityDraft, setCapabilityDraft] = useState('');

  useEffect(() => {
    if (!isOpen) setCapabilityDraft('');
  }, [isOpen, agentDefinition?.id]);

  // ─── Form snapshot ────────────────────────────────────────────────────────
  const { state, setField, baseline, current, setBaseline } = useFormSnapshot<FormSnapshot>({
    initialFactory: () => {
      if (isEdit && actor) {
        return {
          actorType: actor.actorType,
          selectedUserId: actor.userId ?? null,
          name: actor.name ?? '',
          expertiseScope: actor.expertiseScope ?? '',
          adoptionLevel: actor.adoptionLevel ?? RorosAdoptionLevel.SHADOW,
          selectedDomainIds: actor.domains?.map((d) => d.domainId) ?? [],
          imageAttachmentId: actor.imageAttachmentId ?? null,
          modelTier: ((agentDefinition?.defaultModelTier as 'default' | 'complex') ?? 'default'),
          instructions: agentDefinition?.instructions ?? '',
          capabilities: Array.isArray(agentDefinition?.capabilities)
            ? agentDefinition.capabilities.filter((c): c is string => typeof c === 'string')
            : [],
          canDelegate: !!(agentDefinition?.delegationConfig?.can_delegate),
          maxParallel: typeof agentDefinition?.delegationConfig?.max_parallel === 'number'
            ? agentDefinition.delegationConfig.max_parallel : '',
          provider: (agentDefinition as any)?.provider ?? null,
          routingDescription: agentDefinition?.routingDescription ?? '',
          isCoordinator: agentDefinition?.isCoordinator ?? false,
        };
      }
      return {
        actorType: initialActorType ?? '',
        selectedUserId: null,
        name: '',
        expertiseScope: '',
        adoptionLevel: RorosAdoptionLevel.SHADOW,
        selectedDomainIds: [],
        imageAttachmentId: null,
        modelTier: 'default',
        instructions: '',
        capabilities: [],
        canDelegate: false,
        maxParallel: '',
        provider: null,
        routingDescription: '',
        isCoordinator: false,
      };
    },
    isOpen,
    depKey: actor?.id ?? (initialActorType ?? 'new'),
  });

  const {
    actorType, selectedUserId, name, expertiseScope, adoptionLevel,
    selectedDomainIds, imageAttachmentId, modelTier, instructions, capabilities,
    canDelegate, maxParallel, provider, routingDescription, isCoordinator,
  } = state;

  // Derive showPersonaTab from state (used both in edit+create context for tab visibility)
  const showPersonaTab = actorType === RorosActorType.AI_OPERATOR || actorType === RorosActorType.KNOWLEDGE_PERSONA;

  // Type flags using form-state actorType (not actor prop) — works in both create and edit mode.
  // actor prop is null in create mode, so isAiOp/isKnowledgePersona are always false there.
  // Using actorType (form state) mirrors how tab2Content/tab assembly checks already work for AI Op.
  const isAiOpType = actorType === RorosActorType.AI_OPERATOR;
  const isKpType = actorType === RorosActorType.KNOWLEDGE_PERSONA;

  // Sync trigger impacts from agentDefinition
  useEffect(() => {
    if (!isOpen) return;
    const triggers = agentDefinition?.delegationConfig?.moderator_trigger_rules?.auto_invoke_on ?? ['root', 'trunk'];
    setDecisionTriggers(triggers as Array<'root' | 'trunk' | 'branch' | 'leaf'>);
  }, [isOpen, agentDefinition?.id]);

  // ─── Domain helpers ───────────────────────────────────────────────────────
  const activeDomains = domains.filter((d) => d.isActive);

  const toggleDomain = (domainId: string) => {
    setField('selectedDomainIds',
      selectedDomainIds.includes(domainId)
        ? selectedDomainIds.filter((id) => id !== domainId)
        : [...selectedDomainIds, domainId],
    );
  };

  // ─── Capability helpers ───────────────────────────────────────────────────
  const addCapability = () => {
    const trimmed = capabilityDraft.trim();
    if (!trimmed || capabilities.includes(trimmed)) { setCapabilityDraft(''); return; }
    setField('capabilities', [...capabilities, trimmed]);
    setCapabilityDraft('');
  };

  const removeCapability = (cap: string) => {
    setField('capabilities', capabilities.filter((c) => c !== cap));
  };

  // ─── Domain stances helpers (AC-MODEL-3 finalize) ─────────────────────────
  const activeDomainIds = useMemo(() => {
    const set = new Set<number>();
    for (const d of domains) {
      if (d.isActive) set.add(d.domainId);
    }
    return set;
  }, [domains]);


  // ─── Build delegation config for agent definition ─────────────────────────
  const buildDelegationConfigForSave = (): RorosDelegationConfig => buildDelegationConfig(
    canDelegate,
    maxParallel,
    agentDefinition?.delegationConfig,
    decisionTriggers,
  );

  // ─── Save (create / edit) ─────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);

  const handleCreate = async () => {
    // FW-7 / AC-LLMCFG-6: provider guard aligned to use same derived predicates as handleEdit.
    // AI Operators require an actor-level provider; Knowledge Personas require a persona provider.
    if (isAiOpType && !provider) {
      showToast.error(t('Please select a provider before saving.'));
      return;
    }
    // FW-7: KP guard — AC-6 covers all actor editors that have a provider field.
    if (isKpType && !personaState.provider) {
      showToast.error(t('Please select a provider before saving.'));
      return;
    }
    const payload: any = { actor_type: actorType };
    if (selectedDomainIds.length > 0) payload.domain_ids = selectedDomainIds;
    if (actorType === RorosActorType.HUMAN) {
      payload.user_id = selectedUserId;
    } else {
      payload.name = name.trim();
      if (expertiseScope.trim()) payload.expertise_scope = expertiseScope.trim();
      if (actorType === RorosActorType.AI_OPERATOR) payload.adoption_level = adoptionLevel;
      payload.image_attachment_id = imageAttachmentId ?? undefined;
    }

    // F-OPFIX: for AI Operator, embed operator settings in the actor create
    // payload so the server writes them atomically (single request, no 409).
    if (actorType === RorosActorType.AI_OPERATOR) {
      payload.agent_definition = {
        default_model_tier: modelTier,
        provider: provider ?? null,
        instructions: instructions.trim() || null,
        capabilities: capabilities.length > 0 ? capabilities : [],
        delegation_config: {
          can_delegate: canDelegate,
          ...(typeof maxParallel === 'number' ? { max_parallel: maxParallel } : {}),
        },
      };
    }

    setIsSaving(true);
    try {
      // Single atomic request: actor + operator settings in one call.
      // The server writes agent_definition fields into the roros_agent_definitions
      // row inside the createActor transaction, so no second POST is needed.
      const newActor = await createActorMutation.mutateAsync(payload);

      // Step 3 (AI Operator + KP): upsert persona if any persona fields were filled
      const hasPersonaData = actorType !== RorosActorType.HUMAN && (
        personaState.voiceDescription.trim()
        || personaState.styleDescription.trim()
        || personaState.valuesPriorities.trim()
        || personaState.decisionStyle.trim()
        || personaState.languageRules.trim()
        || personaState.domainStances.some((s) => s.stance.trim().length > 0)
        || (actorType === RorosActorType.KNOWLEDGE_PERSONA && (
          personaState.provider !== null
          || personaState.modelTier !== null
          || (personaState.instructions ?? '').trim().length > 0
        ))
      );

      if (hasPersonaData) {
        await upsertPersonaMutation.mutateAsync({
          actorId: newActor.id,
          payload: {
            voiceDescription: personaState.voiceDescription.trim() || null,
            styleDescription: personaState.styleDescription.trim() || null,
            valuesPriorities: personaState.valuesPriorities.trim() || null,
            decisionStyle: personaState.decisionStyle.trim() || null,
            primaryLanguage: personaState.primaryLanguage,
            languageRules: personaState.languageRules.trim() || null,
            domainStances: personaState.domainStances.filter((s) => s.stance.trim().length > 0),
            provider: actorType === RorosActorType.KNOWLEDGE_PERSONA ? personaState.provider : personaState.provider,
            modelTier: actorType === RorosActorType.KNOWLEDGE_PERSONA ? (personaState.modelTier ?? null) : undefined,
            instructions: actorType === RorosActorType.KNOWLEDGE_PERSONA ? (personaState.instructions ?? null) : undefined,
          },
        });
      }

      showToast.success(t('Actor created successfully'));
      setBaseline(current);
      onClose();
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : t('Failed to create actor'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!actor) return;
    // AC-LLMCFG-6: Provider required for AI Operators on save
    if (isAiOpType && !provider) {
      showToast.error(t('Please select a provider before saving.'));
      return;
    }
    const nameChanged = actor.actorType !== RorosActorType.HUMAN
      && !isProtected
      && name.trim() !== (actor.name ?? '');
    const expertiseChanged = actor.actorType !== RorosActorType.HUMAN
      && (expertiseScope.trim() || '') !== (actor.expertiseScope ?? '');
    const adoptionChanged = actor.actorType === RorosActorType.AI_OPERATOR
      && adoptionLevel !== actor.adoptionLevel;
    const imageChanged = actor.actorType !== RorosActorType.HUMAN
      && imageAttachmentId !== (actor.imageAttachmentId ?? null);

    const existingDomainIds = new Set(actor.domains?.map((d) => d.domainId) ?? []);
    const selectedSet = new Set(selectedDomainIds);
    const toAdd = [...selectedSet].filter((id) => !existingDomainIds.has(id));
    const toRemove = [...existingDomainIds].filter((id) => !selectedSet.has(id));

    // AI operator: detect agentDef changes
    const agentChanged = isAiOp && (
      modelTier !== (agentDefinition?.defaultModelTier ?? 'default')
      || instructions !== (agentDefinition?.instructions ?? '')
      || !arrayUnordered(capabilities, (agentDefinition?.capabilities ?? []) as string[])
      || canDelegate !== !!(agentDefinition?.delegationConfig?.can_delegate)
      || maxParallel !== (typeof agentDefinition?.delegationConfig?.max_parallel === 'number'
        ? agentDefinition.delegationConfig.max_parallel : '')
      || provider !== ((agentDefinition as any)?.provider ?? null)
      || routingDescription !== (agentDefinition?.routingDescription ?? '')
      || isCoordinator !== (agentDefinition?.isCoordinator ?? false)
    );

    if (!nameChanged && !expertiseChanged && !adoptionChanged && !imageChanged
      && toAdd.length === 0 && toRemove.length === 0 && !agentChanged) {
      showToast.success(t('No changes to save'));
      setBaseline(current);
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      if (nameChanged || expertiseChanged || imageChanged) {
        await updateActorMutation.mutateAsync({
          id: actor.id,
          payload: {
            name: nameChanged ? name.trim() : undefined,
            expertise_scope: expertiseChanged ? (expertiseScope.trim() || undefined) : undefined,
            image_attachment_id: imageChanged ? imageAttachmentId : undefined,
          },
        });
      }
      for (const domainId of toRemove) {
        await unassignDomainMutation.mutateAsync({ id: actor.id, domainId });
      }
      for (const domainId of toAdd) {
        await assignDomainMutation.mutateAsync({ id: actor.id, domainId });
      }
      // Agent definition save (AI operators)
      if (isAiOp && agentChanged) {
        const agentPayload = {
          default_model_tier: modelTier,
          instructions: instructions || undefined,
          capabilities,
          delegation_config: buildDelegationConfigForSave(),
          provider: provider ?? null,
          routingDescription: routingDescription.trim() || null,
          isCoordinator,
        };
        if (agentDefinition) {
          await updateAgentMutation.mutateAsync({ id: agentDefinition.id, payload: agentPayload });
        } else {
          await createAgentMutation.mutateAsync({ actor_id: actor.id, ...agentPayload });
        }
        // AC-11 superseded: budget cap sync removed — budget managed via Role Contract editor
      }
      if (adoptionChanged) {
        await changeAdoptionMutation.mutateAsync({ id: actor.id, payload: { adoption_level: adoptionLevel } });
      }
      showToast.success(t('Actor updated successfully'));
      setBaseline(current);
      onClose();
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : t('Failed to update actor'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (isEdit) { await handleEdit(); } else { await handleCreate(); }
  };

  // ─── Compute header ───────────────────────────────────────────────────────
  // AC-ACTORS-2: in create mode, title reflects the actor type being created.
  // B1: "New Human" title added for human create mode (consistent with AI Op/KP).
  const createTitle = (() => {
    if (actorType === RorosActorType.HUMAN) return t('New Human');
    if (actorType === RorosActorType.AI_OPERATOR) return t('New AI Operator');
    if (actorType === RorosActorType.KNOWLEDGE_PERSONA) return t('New Knowledge Persona');
    return t('Create Actor');
  })();
  const title = isEdit
    ? `${t('Edit Actor')}: ${actor?.name ?? ''}`
    : createTitle;

  const isSubmitting = isSaving || createActorMutation.isPending;
  const submitDisabled = isEdit
    ? (isSaving || (actorType !== RorosActorType.HUMAN && !name.trim() && !isProtected && !isEdit))
    : (!actorType || (actorType === RorosActorType.HUMAN ? !selectedUserId : !name.trim()) || createActorMutation.isPending);

  // ─── Actor type options ───────────────────────────────────────────────────
  const ACTOR_TYPE_OPTIONS = [
    { type: RorosActorType.HUMAN, label: t('Human'), description: t('A human team member'), icon: User },
    { type: RorosActorType.AI_OPERATOR, label: t('AI Operator'), description: t('An AI agent that executes tasks'), icon: Bot },
    { type: RorosActorType.KNOWLEDGE_PERSONA, label: t('Knowledge Persona'), description: t('An AI advisory persona for consultation'), icon: BookOpen },
  ];

  const ADOPTION_OPTIONS = [
    { value: RorosAdoptionLevel.SHADOW, label: t('Shadow') },
    { value: RorosAdoptionLevel.COPILOT, label: t('Copilot') },
    { value: RorosAdoptionLevel.AUTOPILOT, label: t('Autopilot') },
  ];

  const MODEL_OPTIONS = [
    {
      id: 'default',
      title: t('Default'),
      description: t('Fast, cost-efficient model for routine work.'),
      icon: <Zap className="h-4 w-4" />,
    },
    {
      id: 'complex',
      title: t('Complex'),
      description: t('Higher-tier reasoning for nuanced or strategic tasks.'),
      icon: <Cpu className="h-4 w-4" />,
    },
  ];

  const triggerImpactLabels: Record<string, string> = {
    root: t('Root'),
    trunk: t('Trunk'),
    branch: t('Branch'),
    leaf: t('Leaf'),
  };

  // ─── Provider options (AC-LLMCFG-5: "Workspace default" removed) ──────────
  // Only org-configured providers appear. Provider is required on save (AC-LLMCFG-6).
  const providerOptions = buildProviderOptions(aiConfigs);

  // Capability display for AC-MOD-6 — derived from agentDefinition.capabilities
  const hasMeetingCap = capabilities.includes('can_moderate_meetings');
  const hasDecisionCap = capabilities.includes('can_facilitate_decisions');

  // ─── Tab 1: Identity ──────────────────────────────────────────────────────

  // AC-ACTORS-4: actor type shown in the HEADER, not as a body field.
  // AC-ACTORS-2: type-selection card grid removed — type is pre-set by the create button.
  const selectedTypeOption = ACTOR_TYPE_OPTIONS.find((o) => o.type === actorType);

  // AC-ACTORS-4: header extras — actor type label + System badge shown in FormSheet header.
  // B1: show the actor-type chip for ALL types in create mode (including Human)
  //   so the sheet chrome is consistent across all three types.
  const showHeaderExtras = isEdit || !!actorType;

  const editorHeaderExtras = showHeaderExtras ? (
    <div className="flex items-center gap-2" data-testid="actor-type-header-extras">
      {selectedTypeOption && (
        <span
          className="flex items-center gap-1.5 text-sm text-muted-foreground"
          data-testid="actor-type-header-label"
        >
          <selectedTypeOption.icon className="h-4 w-4" />
          {selectedTypeOption.label}
        </span>
      )}
      {isProtected && (
        <Badge
          className="bg-amber-100 text-amber-800 border-amber-200 flex items-center gap-1"
          data-testid="system-actor-badge"
        >
          <ShieldCheck className="h-3 w-3" />
          {t('System')}
        </Badge>
      )}
    </div>
  ) : null;

  // B4 (AC-ACTORS-7): Human Identity — avatar on LEFT, name + email on RIGHT, clutter removed.
  // In edit mode: read-only user row with avatar-left layout; no separate "Account Avatar" section.
  // In create mode: UserPicker to link an org member.
  const humanSection = (
    <div className="space-y-4">
      {isEdit ? (
        // Edit mode — read-only identity row: [Avatar][Name / Email]
        <div
          className="p-4 rounded-lg border bg-muted/30 flex items-center gap-4"
          data-testid="human-actor-identity-row"
        >
          {/* Avatar — LEFT */}
          <Avatar
            size="lg"
            src={actor?.userAvatarUrl ?? undefined}
            name={actor?.userFullName ?? actor?.name ?? '?'}
            data-testid="human-actor-avatar-display"
          />
          {/* Name + email — RIGHT */}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="human-actor-name-display">
              {actor?.userFullName ?? actor?.name ?? linkedMember?.fullName ?? t('Unknown user')}
            </div>
            {(actor?.userEmail || linkedMember?.email) && (
              <div className="text-xs text-muted-foreground truncate" data-testid="human-actor-email-display">
                {actor?.userEmail ?? linkedMember?.email}
              </div>
            )}
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <Lock className="h-3 w-3" />
              {t('Cannot be changed')}
            </div>
          </div>
        </div>
      ) : (
        // Create mode — pick an org member
        <div>
          <label className="block text-sm font-medium mb-2">{t('Select a person')}</label>
          <UserPicker selectedUserId={selectedUserId} onSelect={(id) => setField('selectedUserId', id)} />
        </div>
      )}
    </div>
  );

  const tab1Content = (
    <div className="space-y-6">
      {actorType && (
        <>
          {actorType === RorosActorType.HUMAN && humanSection}

          {/* AC-ACTORS-9: avatar + name side by side; expertise below */}
          {actorType !== RorosActorType.HUMAN && (
            <div className="flex items-start gap-4">
              {/* Avatar (left) — click/drop to change */}
              <div className="flex-shrink-0">
                <ActorDirectImageUpload
                  value={imageAttachmentId}
                  onChange={(id) => setField('imageAttachmentId', id)}
                />
              </div>
              {/* Name (right) */}
              <div className="flex-1 min-w-0 pt-1">
                <Input
                  label={t('Name')}
                  value={name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder={t('Enter actor name')}
                  disabled={isProtected}
                  hint={isProtected ? t('Name is locked for system operators') : undefined}
                  data-testid="actor-editor-name-input"
                />
              </div>
            </div>
          )}

          {/* Expertise scope — below the avatar+name row */}
          {actorType !== RorosActorType.HUMAN && (
            <Textarea
              label={t('Expertise Scope')}
              value={expertiseScope}
              onChange={(e) => setField('expertiseScope', e.target.value)}
              placeholder={t('Describe the area of expertise...')}
              rows={3}
            />
          )}

          {actorType === RorosActorType.AI_OPERATOR && (
            <Select
              label={t('Adoption Level')}
              options={ADOPTION_OPTIONS}
              value={adoptionLevel}
              onValueChange={(v) => setField('adoptionLevel', v)}
            />
          )}

          <div>
            <label className="block text-sm font-medium mb-2">{t('Assign to Domains')}</label>
            <div className="flex flex-wrap gap-2">
              {activeDomains.map((domain) => {
                const isSelected = selectedDomainIds.includes(domain.id);
                return (
                  <Button
                    key={domain.id}
                    type="button"
                    variant={isSelected ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => toggleDomain(domain.id)}
                    className="rounded-full px-3 py-1 text-xs h-auto"
                  >
                    {domain.name}
                  </Button>
                );
              })}
            </div>
            {activeDomains.length === 0 && (
              <p className="text-xs text-muted-foreground">{t('No active domains available')}</p>
            )}
          </div>
        </>
      )}
    </div>
  );

  // ─── Tab 2: Operator (AI operator only) ───────────────────────────────────

  const tab2Content = actorType === RorosActorType.AI_OPERATOR ? (
    <div className="space-y-6">
      {/* AC-ACTORS-12 (refined): Role Contract — FIRST, dropdown first then info below, immediate save */}
      {isEdit && (
        <section data-testid="ai-operator-role-contract-section">
          <h3 className="text-sm font-semibold mb-2">{t('Role Contract')}</h3>
          {/* Dropdown FIRST — active linked contract shown as disabled; draft contracts are changeable */}
          {canManage && (
            <>
              <Select
                data-testid="ai-operator-role-contract-picker"
                value={pickerContractId || undefined}
                onValueChange={handleContractSelect}
                placeholder={
                  isContractActive
                    ? linkedRoleContract?.name ?? t('No assignable contracts')
                    : assignableContracts.length === 0
                      ? t('No assignable contracts')
                      : t('Select a Role Contract…')
                }
                options={
                  isContractActive && linkedRoleContract
                    ? [{ value: linkedRoleContract.id, label: linkedRoleContract.name }]
                    : assignableContracts.map((c) => ({
                        value: c.id,
                        label: `${c.name}${c.actorId === actor?.id ? ` (${t('linked')})` : ''}`,
                      }))
                }
                disabled={contractAssigning || isContractActive || assignableContracts.length === 0}
              />
              {assignableContracts.length === 0 && !linkedRoleContract && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('No assignable contracts — create a draft in Role Contracts')}
                </p>
              )}
            </>
          )}
          {/* Info/status BELOW the dropdown */}
          {linkedRoleContract ? (
            <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 space-y-1 text-xs text-foreground">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium">{linkedRoleContract.name}</p>
                  {linkedRoleContract.purposeScope && (
                    <p className="text-muted-foreground line-clamp-2 mt-0.5">{linkedRoleContract.purposeScope}</p>
                  )}
                  {!isLinkedContractEditable && (
                    <p className="text-muted-foreground mt-1 italic">
                      {t('Active contract — manage it on the Role Contracts page')}
                    </p>
                  )}
                </div>
                {canManage && isLinkedContractEditable && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 flex-shrink-0"
                    onClick={handleContractUnlink}
                    disabled={contractAssigning}
                    aria-label={t('Remove contract')}
                    data-testid="ai-operator-role-contract-remove"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground mt-2">{t('No Role Contract linked yet.')}</p>
          )}
        </section>
      )}

      {/* AC-11 superseded: Budget cap field removed — budget is owned by the Role Contract */}

      {/* Instructions */}
      <section>
        <h3 className="text-sm font-semibold mb-3">{t('System Instructions')}</h3>
        <Textarea
          value={instructions}
          onChange={(e) => setField('instructions', e.target.value)}
          placeholder={t('Enter the operator\'s system instructions...')}
          rows={6}
          data-testid="operator-instructions"
        />
      </section>

      {/* Delegation */}
      <section>
        <h3 className="text-sm font-semibold mb-3">{t('Delegation')}</h3>
        <div className="space-y-3">
          <Switch
            label={t('Allow this operator to delegate sub-tasks')}
            checked={canDelegate}
            onChange={(e) => setField('canDelegate', e.target.checked)}
          />
          {canDelegate && (
            <Input
              label={t('Max parallel delegations')}
              type="number"
              value={String(maxParallel)}
              onChange={(e) => {
                const v = e.target.value;
                setField('maxParallel', v === '' ? '' : Math.max(0, parseInt(v, 10) || 0));
              }}
              hint={t('Cap on the number of in-flight task delegations from this operator.')}
            />
          )}
        </div>
      </section>

      {/* AC-AGS-M4-1: Routing */}
      <section data-testid="operator-routing-section">
        <h3 className="text-sm font-semibold mb-3">{t('Routing')}</h3>
        <div className="space-y-3">
          <Switch
            label={t('Coordinator')}
            checked={isCoordinator}
            onChange={(e) => setField('isCoordinator', e.target.checked)}
            data-testid="operator-is-coordinator"
          />
          <Textarea
            label={t('Routing Description')}
            value={routingDescription}
            onChange={(e) => setField('routingDescription', e.target.value)}
            placeholder={t('Describe when to use this operator, its specialisation, or its scope…')}
            rows={3}
            hint={t('Displayed in the session actor picker to help users choose the right operator (max 500 characters).')}
            maxLength={500}
            data-testid="operator-routing-description"
          />
        </div>
      </section>

      {/* Skills */}
      {agentDefinition && (
        <section data-testid="operator-skills-section">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            {t('Skills')}
          </h3>
          {matrixQuery.isLoading ? (
            <p className="text-xs text-muted-foreground">{t('Loading…')}</p>
          ) : assignedSkills.length === 0 ? (
            <p className="text-xs text-muted-foreground mb-3 italic" data-testid="operator-skills-empty">
              {t('No skills assigned yet.')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 mb-3">
              {assignedSkills.map((skill: any) => (
                <Badge
                  key={skill.skillId ?? skill.skill_id}
                  variant={skill.isActive ?? skill.is_active ? 'outline' : 'secondary'}
                  className="flex items-center gap-1"
                >
                  {skill.skillDisplayName ?? skill.skill_display_name ?? skill.skillId}
                  {canManageSkills && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="ml-1 h-4 w-4 p-0"
                      onClick={() => handleSkillUnassign(skill.skillId ?? skill.skill_id)}
                      disabled={unassignSkillMutation.isPending}
                      aria-label={t('Remove')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </Badge>
              ))}
            </div>
          )}
          {canManageSkills && (
            <div className="flex gap-2">
              <Select
                value={pickerSkillId || undefined}
                onValueChange={setPickerSkillId}
                wrapperClassName="flex-1"
                placeholder={availableSkillOptions.length === 0 ? t('All skills assigned') : t('Add a skill…')}
                options={availableSkillOptions}
                disabled={availableSkillOptions.length === 0}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!pickerSkillId || assignSkillMutation.isPending}
                onClick={handleSkillAssign}
              >
                {t('Add')}
              </Button>
            </div>
          )}
        </section>
      )}

      {/* AGS-M2: Tool Grants */}
      {agentDefinition && isAiOpType && (
        <section data-testid="operator-tool-grants-section">
          <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
            {t('roros.operators.tool_grants_section')}
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            {t('roros.operators.tool_grants_hint')}
          </p>
          <div className="space-y-2 mb-3">
            {DOMAIN_TOOLS.map((tool) => (
              <div key={tool.key} className="flex items-center gap-2">
                <Checkbox
                  id={`tool-grant-${tool.key}`}
                  checked={allowedTools.includes(tool.key)}
                  onCheckedChange={(checked) => handleToolToggle(tool.key, !!checked)}
                  disabled={!canManageSkills}
                />
                <label htmlFor={`tool-grant-${tool.key}`} className="text-sm cursor-pointer">
                  {tool.label()}
                </label>
              </div>
            ))}
          </div>
          {canManageSkills && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={toolGrantsSaving}
              onClick={handleToolGrantsSave}
            >
              {toolGrantsSaving ? t('Saving…') : t('Save tool grants')}
            </Button>
          )}
        </section>
      )}

      {/* AC-ACTORS-10: Provider + Model Tier as compact dropdowns side by side — LAST */}
      {/* AC-LLMCFG-5: "Workspace default" sentinel removed — only real provider ids */}
      {isAiOpType && (
        <section>
          <div className="flex gap-3" data-testid="operator-provider-model-row">
            <div className="flex-1">
              <Select
                label={t('Provider')}
                value={provider ?? ''}
                onValueChange={(v) => setField('provider', v || null)}
                placeholder={t('Select a provider…')}
                options={providerOptions.map((o) => ({ value: o.id, label: o.title }))}
                data-testid="operator-provider-select"
              />
            </div>
            <div className="flex-1">
              <Select
                label={t('Model Tier')}
                value={modelTier}
                onValueChange={(v) => setField('modelTier', v as 'default' | 'complex')}
                options={MODEL_OPTIONS.map((o) => ({ value: o.id, label: o.title }))}
                data-testid="operator-model-tier-select"
              />
            </div>
          </div>
        </section>
      )}
    </div>
  ) : null;

  // ─── Tab 3: Governance — REMOVED (AC-ACTORS-16) ───────────────────────────
  // Role Contract relocated to Operator tab (AC-ACTORS-12).
  // Moderator-default set/clear buttons removed; backend auto-defaults to
  // system_role moderator actors (AC-ACTORS-17).
  // The org-level moderator-default ENDPOINTS remain functional for orgs that
  // set an explicit default — only the editor UI is removed here.

  const tab3Content = null;

  // ─── Tab 4: Persona (AI operator + knowledge_persona) ────────────────────
  // AC-EDIT-1 finalize: knowledge_persona gets persona tab.
  // AC-MODEL-3 finalize: domain_stances included.

  const tab4Content = showPersonaTab ? (
    <div className="space-y-6" data-testid="persona-section">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {isEdit
            ? t('Configure this operator\'s voice, style, and decision approach. Changes apply to future runs.')
            : t('Configure persona settings. These will be saved when you create the actor.')}
        </p>
        {/* Save button only shown in edit mode — in create mode, persona is saved via Create Actor */}
        {isEdit && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handlePersonaSave}
            disabled={!personaDirty || upsertPersonaMutation.isPending}
            loading={upsertPersonaMutation.isPending}
            className="flex-shrink-0"
            data-testid="persona-save-button"
          >
            {t('Save Persona')}
          </Button>
        )}
      </div>

      {personaQuery.isLoading && !personaInitialized && <LoadingState />}

      <div className="space-y-4">
        <Textarea
          label={t('Voice & Tone')}
          value={personaState.voiceDescription}
          onChange={(e) => { setPersonaState((p) => ({ ...p, voiceDescription: e.target.value })); setPersonaDirty(true); }}
          placeholder={t('Describe the operator\'s communication style...')}
          rows={3}
          data-testid="persona-voice"
        />
        <Textarea
          label={t('Working Style')}
          value={personaState.styleDescription}
          onChange={(e) => { setPersonaState((p) => ({ ...p, styleDescription: e.target.value })); setPersonaDirty(true); }}
          placeholder={t('How does this operator approach tasks?')}
          rows={3}
          data-testid="persona-style"
        />
        <Textarea
          label={t('Values & Priorities')}
          value={personaState.valuesPriorities}
          onChange={(e) => { setPersonaState((p) => ({ ...p, valuesPriorities: e.target.value })); setPersonaDirty(true); }}
          placeholder={t('Core values and decision priorities...')}
          rows={3}
        />
        <Textarea
          label={t('Decision Style')}
          value={personaState.decisionStyle}
          onChange={(e) => { setPersonaState((p) => ({ ...p, decisionStyle: e.target.value })); setPersonaDirty(true); }}
          placeholder={t('How does this operator make decisions?')}
          rows={3}
        />
      </div>
      {/* AC-18: Primary Language + Language Rules fields removed from UI. Columns retained in DB. */}

      {/* Domain Stances — hidden from UI per Item 4 (data/state/DB columns preserved).
          The section is conditionally rendered as null so it's completely absent from the DOM.
          State (personaState.domainStances) and DB columns are preserved for API use.
          The data-testid is left on a zero-size hidden div for test query compatibility. */}
      <div data-testid="domain-stances-section" style={{ display: 'none' }} aria-hidden="true" />
    </div>
  ) : null;

  // ─── KP Operator tab content (AC-23) ─────────────────────────────────────
  // Provider + Model Tier + System Instructions for knowledge_persona (create + edit).

  const kpOperatorTabContent = isKpType ? (
    <div className="space-y-6" data-testid="kp-operator-section">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {isEdit
            ? t('Configure the AI provider and model for this knowledge persona. Changes apply to future consultations.')
            : t('Configure the AI provider and model. These settings will be saved when you create the persona.')}
        </p>
        {/* Save button only shown in edit mode — in create mode, values persist via Create Actor */}
        {isEdit && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handlePersonaSave}
            disabled={!personaDirty || upsertPersonaMutation.isPending}
            loading={upsertPersonaMutation.isPending}
            className="flex-shrink-0"
            data-testid="kp-operator-save-button"
          >
            {t('Save')}
          </Button>
        )}
      </div>

      {/* System Instructions — FIRST (Item 2: reorder per brief) */}
      <section>
        <h3 className="text-sm font-semibold mb-3">{t('System Instructions')}</h3>
        <Textarea
          value={personaState.instructions ?? ''}
          onChange={(e) => {
            setPersonaState((p) => ({ ...p, instructions: e.target.value || null }));
            setPersonaDirty(true);
          }}
          placeholder={t('Enter system instructions for this knowledge persona...')}
          rows={6}
          data-testid="kp-operator-instructions"
        />
      </section>

      {/* Provider + Model Tier side by side — BELOW instructions (consistent with AI Operator preference) */}
      <section>
        <div className="flex gap-3" data-testid="kp-operator-provider-model-row">
          <div className="flex-1">
            {/* FW-7 / AC-5: "Workspace default" sentinel removed — only real provider ids */}
            <Select
              label={t('Provider')}
              value={personaState.provider ?? ''}
              onValueChange={(v) => {
                setPersonaState((p) => ({ ...p, provider: v || null }));
                setPersonaDirty(true);
              }}
              placeholder={t('Select a provider…')}
              options={providerOptions.map((o) => ({ value: o.id, label: o.title }))}
              data-testid="kp-operator-provider-select"
            />
          </div>
          <div className="flex-1">
            <Select
              label={t('Model Tier')}
              value={personaState.modelTier ?? 'default'}
              onValueChange={(v) => {
                setPersonaState((p) => ({ ...p, modelTier: v as 'default' | 'complex' }));
                setPersonaDirty(true);
              }}
              options={MODEL_OPTIONS.map((o) => ({ value: o.id, label: o.title }))}
              data-testid="kp-operator-model-tier-select"
            />
          </div>
        </div>
      </section>
    </div>
  ) : null;

  // ─── Tab 5: Management (all types, edit mode only) ────────────────────────
  // AC-EDIT-3: visibility + readiness/promote history + deactivate
  // AC-EDIT-5: Promote (ReadinessAssessmentDialog) accessible from editor

  const tab5Content = isEdit && actor ? (
    <div className="space-y-6">
      {/* Visibility — all actor types */}
      <section data-testid="actor-visibility-section">
        <h3 className="text-sm font-semibold mb-3">{t('Visibility')}</h3>
        <VisibilityPanel
          entityType="actor"
          entityId={actor.id}
          excludeActorIds={[actor.id]}
          description={t('Restrict who can see this actor across the org.')}
        />
      </section>

      {/* Readiness Assessments — AI operators only (GOV-04 / AC-EDIT-5) */}
      {actorType === RorosActorType.AI_OPERATOR && (
        <section data-testid="actor-readiness-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold">{t('Readiness Assessments')}</h3>
            </div>
            {canManage && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowPromoteDialog(true)}
                data-testid="actor-promote-button"
                disabled={!actor.domains || actor.domains.length === 0}
                title={(!actor.domains || actor.domains.length === 0) ? t('Assign a domain first') : undefined}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t('Promote')}
              </Button>
            )}
          </div>
          {assessmentsQuery.isLoading ? (
            <p className="text-xs text-muted-foreground">{t('Loading...')}</p>
          ) : assessmentsQuery.data?.data && assessmentsQuery.data.data.length > 0 ? (
            <div className="space-y-2">
              {assessmentsQuery.data.data.map((a: any) => (
                <div key={a.id} className="flex items-center justify-between p-3 bg-muted rounded-lg text-sm">
                  <span className="font-medium">{adoptionLevelLabel(a.targetAdoptionLevel)}</span>
                  <span className="text-muted-foreground">{a.score}/{a.maxScore}</span>
                  <span>{readinessResultLabel(a.result)}</span>
                  <span className="text-xs text-muted-foreground">{formatDate(a.createdAt)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{t('No readiness assessments yet.')}</p>
          )}
        </section>
      )}

      {/* Deactivate — non-protected, non-human active actors (AC-MODEL-2 + AC-HUMAN-3 guards) */}
      {canManage && (
        <section data-testid="actor-deactivate-section">
          <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-destructive, #ef4444)' }}>
            {t('Danger Zone')}
          </h3>
          {actorType === RorosActorType.HUMAN ? (
            // AC-HUMAN-3 (Wave I): humans managed via User management, not actor controls
            <p className="text-xs text-muted-foreground" data-testid="actor-deactivate-human-msg">
              {t('Human actors are managed through User settings. Remove the user from the org to deactivate their actor.')}
            </p>
          ) : isProtected ? (
            <p className="text-xs text-muted-foreground" data-testid="actor-deactivate-protected-msg">
              {t('System actors cannot be deactivated.')}
            </p>
          ) : actor.status !== 'active' ? (
            <p className="text-xs text-muted-foreground">{t('This actor is already inactive.')}</p>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              onClick={() => setShowDeactivateConfirm(true)}
              disabled={deactivateMutation.isPending}
              data-testid="actor-deactivate-button"
            >
              <PowerOff className="h-4 w-4 mr-2" />
              {t('Deactivate Actor')}
            </Button>
          )}
        </section>
      )}
    </div>
  ) : null;

  // ─── Tab 6: Documents (knowledge_persona, edit mode only) ────────────────
  // AC-K-8: list pinned docs + "+N more via domains"; AC-K-9: Add Document button.

  const personaId = isEdit && actor ? actor.id : undefined;
  const pinnedDocsQuery = useRorosPersonaPinDenyList(orgId, personaId, 'pin');
  const reachableCountQuery = useRorosPersonaReachableCount(orgId, personaId);
  const createPinMutation = useRorosPersonaPinDenyCreate(orgId, personaId);
  const deletePinMutation = useRorosPersonaPinDenyDelete(orgId, personaId);
  const createKnowledgeMutation = useRorosKnowledgeCreate();

  const [isUploadSheetOpen, setIsUploadSheetOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handlePersonaDocUploaded = async (attachment: { id: string; title: string; filename: string; description?: string; itemType?: string }) => {
    setUploadError(null);
    try {
      // AC-K-9 / AC-21 / AC-25: Step 1 — create a knowledge item from the uploaded document.
      // domain_id=NULL (system-scope); scope via relevant_domain_ids if needed later.
      // AC-21/AC-25: pass description and item_type from the upload sheet.
      const knowledgeItem = await new Promise<{ id: string }>((resolve, reject) => {
        createKnowledgeMutation.mutate(
          {
            domainId: 'system',
            payload: {
              title: attachment.title || attachment.filename,
              description: attachment.description ?? undefined,
              item_type: attachment.itemType || 'document',
              status: 'active',
              relevant_domain_ids: null,
            } as any,
          },
          {
            onSuccess: (data: any) => resolve(data),
            onError: reject,
          },
        );
      });

      // AC-K-9: Step 2 — pin the new knowledge item to this persona.
      await new Promise<void>((resolve, reject) => {
        createPinMutation.mutate(
          { entityId: knowledgeItem.id, entityType: 'knowledge', mode: 'pin' },
          { onSuccess: () => resolve(), onError: reject },
        );
      });

      setIsUploadSheetOpen(false);
    } catch (err: any) {
      setUploadError(err?.message || t('Failed to attach document'));
    }
  };

  const pinnedItems = (pinnedDocsQuery.data ?? []) as Array<{
    id: string;
    entityId: string;
    entityType: string;
    mode: string;
    createdAt: string;
    itemTitle: string | null;
    // AC-21/AC-25: description and type fields from the knowledge item
    itemDescription: string | null;
    itemType: string | null;
  }>;
  const reachableCount = reachableCountQuery.data;
  const viaDomains = reachableCount?.viaDomains ?? 0;

  const tab6Content =
    // AC-21: Documents tab now shown for both KNOWLEDGE_PERSONA and AI_OPERATOR.
    // The binding table roros_persona_knowledge uses a generic persona_actor_id
    // so AI operators can pin documents with no schema change.
    isEdit && actor && (actorType === RorosActorType.KNOWLEDGE_PERSONA || actorType === RorosActorType.AI_OPERATOR) ? (
      <div className="space-y-4" data-testid="persona-documents-tab">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">{t('Pinned Documents')}</h3>
            {viaDomains > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="persona-docs-via-domains">
                +{viaDomains} {t('more reachable via its domains')}
              </p>
            )}
          </div>
          {/* AC-K-9: Add Document — MANAGE_KNOWLEDGE-gated */}
          {canManageKnowledge && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsUploadSheetOpen(true)}
              data-testid="persona-add-document-button"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              {t('Add Document')}
            </Button>
          )}
        </div>

        {uploadError && (
          <Alert variant="error" data-testid="persona-docs-error">
            {uploadError}
          </Alert>
        )}

        {pinnedDocsQuery.isLoading ? (
          <p className="text-xs text-muted-foreground">{t('Loading...')}</p>
        ) : pinnedItems.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t('No documents pinned to this persona yet.')}
          </p>
        ) : (
          <ul className="space-y-2" role="list">
            {pinnedItems.map((pin) => (
              <li
                key={pin.id}
                className="flex items-center justify-between gap-3 p-3 bg-muted rounded-lg text-sm"
                data-testid={`persona-pinned-doc-${pin.entityId}`}
              >
                <div className="flex-1 min-w-0">
                  {/* R2: show document title (or entity ID fallback) */}
                  <span className="font-medium text-sm truncate block" data-testid={`persona-pinned-doc-title-${pin.entityId}`}>
                    {pin.itemTitle ?? pin.entityId}
                  </span>
                  {/* AC-21/AC-25: show type badge and description */}
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {pin.itemType && (
                      <span
                        className="text-xs bg-muted border border-border rounded px-1.5 py-0.5 text-muted-foreground capitalize"
                        data-testid={`persona-pinned-doc-type-${pin.entityId}`}
                      >
                        {pin.itemType.replace(/_/g, ' ')}
                      </span>
                    )}
                    {pin.itemDescription && (
                      <span className="text-xs text-muted-foreground truncate" data-testid={`persona-pinned-doc-desc-${pin.entityId}`}>
                        {pin.itemDescription}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(pin.createdAt)}
                  </span>
                </div>
                {/* R2: Remove (unpin) action — MANAGE_KNOWLEDGE-gated */}
                {canManageKnowledge && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => deletePinMutation.mutate({ bindId: pin.id })}
                    disabled={deletePinMutation.isPending}
                    aria-label={t('Remove')}
                    data-testid={`persona-remove-doc-${pin.entityId}`}
                    className="text-destructive hover:text-destructive flex-shrink-0"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    ) : null;

  // ─── Assemble tabs ────────────────────────────────────────────────────────

  const tabs: Array<TabDef<FormSnapshot>> = [];

  tabs.push({
    key: 'identity',
    label: t('Identity'),
    fields: IDENTITY_FIELDS,
    content: tab1Content,
    testId: 'unified-editor-tab-identity',
  });

  if (actorType === RorosActorType.AI_OPERATOR && tab2Content) {
    tabs.push({
      key: 'operator',
      label: t('Operator'),
      fields: OPERATOR_FIELDS,
      content: tab2Content,
      testId: 'unified-editor-tab-operator',
    });
  }

  // KP Operator tab (AC-23) — Provider + Model Tier + System Instructions (create + edit)
  if (isKpType && kpOperatorTabContent) {
    tabs.push({
      key: 'operator',
      label: t('Operator'),
      fields: KP_OPERATOR_FIELDS,
      content: kpOperatorTabContent,
      testId: 'unified-editor-tab-operator',
    });
  }

  if (actorType === RorosActorType.AI_OPERATOR && tab3Content) {
    tabs.push({
      key: 'governance',
      label: t('Governance'),
      fields: [],
      content: tab3Content,
      testId: 'unified-editor-tab-governance',
    });
  }

  // Persona: ai_operator (all 4 tabs) + knowledge_persona (tab 2)
  if (showPersonaTab && tab4Content) {
    tabs.push({
      key: 'persona',
      label: t('Persona'),
      fields: [],
      content: tab4Content,
      testId: 'unified-editor-tab-persona',
    });
  }

  // Documents tab — knowledge_persona + ai_operator (AC-21), edit mode
  if (isEdit && tab6Content) {
    tabs.push({
      key: 'documents',
      label: t('Documents'),
      fields: [],
      content: tab6Content,
      testId: 'unified-editor-tab-documents',
    });
  }

  // Management tab — AC-26: hidden for knowledge_persona (deactivate/delete remain on actor cards).
  // AC-ACTORS-8: hidden for human actors (lifecycle managed through User settings).
  // AC-20: hidden for AI operators — deactivate/delete remain on actor cards.
  // No actor types currently show the Management tab in the editor.

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <FormSheet<FormSnapshot>
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        mode={isEdit ? 'edit' : 'create'}
        baseline={baseline}
        current={current}
        diff={diffSnapshots}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitDisabled={submitDisabled}
        submitLabel={isEdit ? t('Save Changes') : t('Create Actor')}
        submittingLabel={isEdit ? t('Saving…') : t('Creating…')}
        headerExtras={editorHeaderExtras ?? undefined}
        width="md"
        urlKey={urlKey}
        urlId={urlId}
        tabs={tabs.length > 1 ? tabs : undefined}
        tabsAriaLabel={t('Actor editor sections')}
        submitTestId="unified-actor-editor-save"
      >
        {/* When no tabs (single-section sheets), render tab1 content directly */}
        {tabs.length <= 1 && tab1Content}
      </FormSheet>

      {/* Deactivate confirm (AC-EDIT-3) */}
      <ConfirmDialog
        isOpen={showDeactivateConfirm}
        onClose={() => setShowDeactivateConfirm(false)}
        onConfirm={handleDeactivate}
        title={`${t('Deactivate')} ${actor?.name ?? t('Actor')}?`}
        description={t('Deactivating this actor will prevent them from being assigned to new work items. Active work items will need to be reassigned.')}
        confirmText={t('Deactivate')}
        variant="destructive"
        isLoading={deactivateMutation.isPending}
      />

      {/* Promote dialog — GOV-04 ReadinessAssessmentDialog (AC-EDIT-5) */}
      {isEdit && actor && showPromoteDialog && (
        <ReadinessAssessmentDialog
          isOpen={showPromoteDialog}
          onClose={() => setShowPromoteDialog(false)}
          actor={actor}
        />
      )}

      {/* AC-K-9 / AC-21: Upload sheet for Documents tab (knowledge_persona + ai_operator) */}
      {isEdit && (actorType === RorosActorType.KNOWLEDGE_PERSONA || actorType === RorosActorType.AI_OPERATOR) && (
        <KnowledgeUploadSheet
          isOpen={isUploadSheetOpen}
          onClose={() => setIsUploadSheetOpen(false)}
          onUploaded={handlePersonaDocUploaded}
          title={t('Add Document')}
          subtitle={actor?.name}
        />
      )}
    </>
  );
}
